import { useRouter } from 'next/router';
import { IconID } from '@common/Icon/Icon';
import { Link } from '@common/Link/Link';
import { WidenImage } from '@common/Widen/WidenImage';
import { useEchainProductStore } from '@hooks/states/useEchainProductStore';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import type { LabeledAttribute, ProductAttribute, ProductAttributeValueAmountUnit } from '@libs/attribute.helper';
import { getAttributeValue, getAttributeValueAmountLabel } from '@libs/attribute.helper';
import { getStandardSupportTray } from '@libs/echain';
import { MediaElementType } from '@libs/widen-utils';
import type { AssociationProductFragmentEnhanced } from '@models/enhanced-fragments';
import { ProductHelper } from '@models/graphql';
import { ProductMediaTeaser } from '@product/ProductMediaTeaser/ProductMediaTeaser';
import { ProductUsp } from '@product/ProductUsp/ProductUsp';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { SelectorBox } from '../SelectorBox/SelectorBox';
import styles from './SupportTraySelector.module.scss';
import { getLocale } from '@libs/constants';
import { getProductIdForLocale } from '@libs/product.helper';

export interface SupportTrayProduct {
  key: string;
  products: AssociationProductFragmentEnhanced[];
}

export const SupportTraySelector = () => {
  const { t } = useTranslation('product/echainBuyBox');

  const router = useRouter();
  const locale = getLocale(router);
  const unitSystem = useProductPageStore(state => state.unitSystem);
  const selectedSupportTray = useEchainProductStore(state => state.selectedSupportTrayProduct);
  const selectedSupportTrayVariant = useEchainProductStore(state => state.selectedSupportTrayVariant);
  const setSelectedSupportTrayProduct = useEchainProductStore(state => state.setSelectedSupportTrayProduct);
  const setSelectedSupportTrayVariant = useEchainProductStore(state => state.setSelectedSupportTrayVariant);
  const setHasUserDeselectedSupportTrays = useEchainProductStore(state => state.setHasUserDeselectedSupportTrays);
  const clearSelectedSupportTray = useEchainProductStore(state => state.clearSelectedSupportTray);
  const currentProduct = useProductPageStore(state => state.currentProduct);
  const groupedSupportTrays = useEchainProductStore(state => state.groupedSupportTrays);

  const drawingImages =
    selectedSupportTrayVariant &&
    ProductHelper.getAssetsMedia(selectedSupportTrayVariant).productMedia.filter(
      media => media.type === MediaElementType.DRAWING
    );

  const drawingTeaserImage = drawingImages?.[0];

  if (!groupedSupportTrays || !groupedSupportTrays.length) {
    return null;
  }

  return (
    <div data-testid="supportTraySelector">
      {selectedSupportTray && (
        <div className={`col-span-${groupedSupportTrays.length} mb-8`}>
          <Link
            className="text-gray-500 w-max"
            onClick={() => {
              setHasUserDeselectedSupportTrays(true);
              clearSelectedSupportTray(currentProduct.id);
            }}
            icon={IconID.CLOSE_CROSS}
            text={t('ECHAIN_PRODUCT_PAGE.SUPPORT_TRAYS.REMOVE_SUPPORT_TRAYS')}
            bigIcon
          />
        </div>
      )}
      <div
        className={classNames(
          'grid',
          'grid-cols-1',
          `sm:grid-cols-2`,
          `lg:grid-cols-3`,
          groupedSupportTrays.length > 1 && 'gap-8',
          'mb-8'
        )}
      >
        {groupedSupportTrays.map((supportTray, key) => {
          const teaserImage = ProductHelper.getTeaserImage(supportTray.products[0]);
          const selectorLabel = getAttributeValue<string>(
            supportTray.products[0].attributes?.type_support_tray as ProductAttribute<string>
          );

          // for the shown sku pick the one based on the variants which has been selected by the user
          const foundSelectedSupportTrayVariant = supportTray.products.find(
            variant => variant.id === selectedSupportTrayVariant?.id
          );
          const idDisplayName = getProductIdForLocale(
            foundSelectedSupportTrayVariant || supportTray.products[0],
            locale
          );

          return (
            <SelectorBox
              dataTestId={`supportTrayProduct-${key}`}
              /* eslint-disable-next-line react/no-array-index-key */
              key={key}
              objectKey={key}
              selected={selectedSupportTray?.key === supportTray.key}
              onSelect={() => {
                /*
                  when changing the support tray, set the variant to the standard support tray
                  else do nothing
                */
                if (!selectedSupportTray || selectedSupportTray.key !== supportTray.key) {
                  setSelectedSupportTrayProduct(supportTray);
                  const newSupportTrayVariant = getStandardSupportTray(supportTray.products);
                  setSelectedSupportTrayVariant(newSupportTrayVariant);
                }
              }}
            >
              <p className="mb-8">
                <strong>{selectorLabel}</strong>
                <br />
                <small data-testid="product-id-display-name" className="text-gray-300">
                  {idDisplayName}
                </small>
              </p>
              <div className={classNames(styles.supportTrayImgWrapper, 'overflow-hidden', 'h-0', 'relative')}>
                <WidenImage
                  title={teaserImage?.title}
                  filename={teaserImage?.filename}
                  shortCode={teaserImage?.shortCode}
                  width={0}
                  height={0}
                  layout="fill"
                  objectFit="contain"
                />
              </div>
            </SelectorBox>
          );
        })}
      </div>
      {selectedSupportTray && selectedSupportTray.products.length > 0 && (
        <div>
          <h3 className="mb-8 font-bold text-sm">{t('ECHAIN_PRODUCT_PAGE.SUPPORT_TRAYS.SELECT_VARIANT')}</h3>
          <div
            className={`grid grid-cols-1 sm:grid-cols-${selectedSupportTray.products.length} lg:grid-cols-${selectedSupportTray.products.length} gap-8 mb-8`}
          >
            {selectedSupportTray.products.map((variant: AssociationProductFragmentEnhanced) => {
              const length = getAttributeValue<ProductAttributeValueAmountUnit>(
                variant.attributes?.lenght as ProductAttribute<ProductAttributeValueAmountUnit>
              );
              const wallBracketText = getAttributeValue<string>(
                variant.attributes?.wall_bracket_text as ProductAttribute<string>
              );
              const variantText = `${getAttributeValueAmountLabel(length, unitSystem, {
                showUnitLabel: true,
              })}, ${wallBracketText}`;
              return (
                <SelectorBox
                  dataTestId={variant.id}
                  key={variant.id}
                  objectKey={variant.id}
                  selected={selectedSupportTrayVariant?.id === variant.id}
                  onSelect={() => {
                    setSelectedSupportTrayVariant(variant);
                  }}
                >
                  {variantText}
                </SelectorBox>
              );
            })}
          </div>
        </div>
      )}
      {selectedSupportTrayVariant && (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-4 mb-8">
            <div className="col-span-3 pr-8">
              <ProductUsp
                usp={getAttributeValue<string>(
                  selectedSupportTrayVariant?.attributes?.attr_single_usp as LabeledAttribute<string>
                )}
              />
            </div>
            {drawingTeaserImage && (
              <div className="flex justify-end">
                <div className="w-full max-w-[200px]">
                  <ProductMediaTeaser media={drawingTeaserImage} width={200} height={Math.round((200 / 3) * 2)} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
